import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, GitBranch, Loader2, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DraftPreview from "../DraftPreview";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

export const VersionHistory = ({ projectId, revisionCount, onRestored }) => {
  const [versions, setVersions] = useState([]);
  const [currentWords, setCurrentWords] = useState(null);
  const [preview, setPreview] = useState(null);
  const [viewingIndex, setViewingIndex] = useState(null);
  const [restoringIndex, setRestoringIndex] = useState(null);

  useEffect(() => {
    if (!projectId || !revisionCount) {
      setVersions([]);
      return;
    }
    const token = localStorage.getItem("access_token");
    axios
      .get(`${API}/research/drafts/${projectId}/versions`, { headers: { Authorization: `Bearer ${token}` } })
      .then((resp) => {
        setVersions(resp.data.versions || []);
        setCurrentWords(resp.data.current_word_count);
      })
      .catch(() => setVersions([]));
  }, [projectId, revisionCount]);

  if (!revisionCount || !versions.length) return null;

  const viewVersion = async (index) => {
    if (viewingIndex !== null) return;
    setViewingIndex(index);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/research/drafts/${projectId}/versions/${index}`, { headers: { Authorization: `Bearer ${token}` } });
      setPreview(resp.data);
    } catch {
      /* toast handled globally is overkill here */
    } finally {
      setViewingIndex(null);
    }
  };

  const restoreVersion = async (index) => {
    if (restoringIndex !== null) return;
    if (!window.confirm(`Restore version ${index + 1}? The current text stays available in the history.`)) return;
    setRestoringIndex(index);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/research/drafts/${projectId}/versions/${index}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setPreview(null);
      onRestored();
    } finally {
      setRestoringIndex(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="research-version-history">
      <details>
        <summary className="flex items-center gap-2 cursor-pointer select-none" data-testid="research-version-history-toggle">
          <GitBranch className="w-4 h-4 text-orange-600" />
          <span className="text-lg font-semibold text-slate-950">Version history</span>
          <span className="text-xs text-slate-400">{versions.length} earlier version{versions.length === 1 ? "" : "s"} — nothing is lost when you revise</span>
        </summary>
        <div className="mt-4 space-y-2">
          {versions.map((v) => (
            <div key={v.index} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 px-3.5 py-2.5" data-testid={`research-version-item-${v.index}`}>
              <span className="shrink-0 rounded bg-slate-900 text-white text-[11px] font-semibold px-2 py-0.5">V{v.index + 1}</span>
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs text-slate-700 leading-5 line-clamp-1">
                  {v.index === 0 ? "Original draft" : "After: "}{v.index === 0 ? "" : versions[v.index - 1].instruction}
                </p>
                <p className="text-[11px] text-slate-400">
                  Replaced {formatDate(v.created_at)} by “{v.instruction.slice(0, 70)}{v.instruction.length > 70 ? "…" : ""}” · {v.word_count.toLocaleString()} words
                </p>
              </div>
              <button
                type="button"
                onClick={() => viewVersion(v.index)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900"
                data-testid={`research-version-view-${v.index}`}
              >
                {viewingIndex === v.index ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} View
              </button>
              <button
                type="button"
                onClick={() => restoreVersion(v.index)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-orange-700 hover:text-orange-900"
                data-testid={`research-version-restore-${v.index}`}
              >
                {restoringIndex === v.index ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Restore
              </button>
            </div>
          ))}
          <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5">
            <span className="shrink-0 rounded bg-emerald-600 text-white text-[11px] font-semibold px-2 py-0.5">Current</span>
            <p className="text-xs text-emerald-900">The draft shown above{currentWords ? ` · ${currentWords.toLocaleString()} words` : ""}</p>
          </div>
        </div>
      </details>

      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="research-version-dialog">
          <DialogHeader>
            <DialogTitle>
              Version {preview ? preview.index + 1 : ""} <span className="font-normal text-sm text-slate-500">· read-only snapshot</span>
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => restoreVersion(preview.index)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold px-3 py-2"
                  data-testid="research-version-dialog-restore"
                >
                  {restoringIndex === preview.index ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Restore this version
                </button>
              </div>
              <DraftPreview markdown={preview.markdown} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
